import React, { useEffect, useState } from "react";
import "../../assets/styles/styles.scss";

import { Bar } from "react-chartjs-2";
import { Spin, Button } from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import PricingRulesModal from "../../components/modals/PricingRulesModal.js";
import CompanyPaymentModal from "../../components/modals/CompanyPaymentModal.js";
import AgentPaymentsModal from "../../components/modals/AgentPaymentsModal.js";
import { formatAmount } from "../../config/helpers.js";
import UnprocessedTicketsModal from "../../components/modals/UnprocessedTicktesModal.js";
import { collection, doc, getDoc, getDocs, increment, limit, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { db } from "../../config/firebase.js";
import { useNavigate } from "react-router-dom";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function PricingPaymentsDashboard() {
  const navigate = useNavigate();
  const [loading, isLoading] = useState(false);
  const [searchEntreprise, setSearchEntreprise] = useState("");
  const [searchAgent, setSearchAgent] = useState("");
  const [pricingOpen, setPricingOpen] = useState(false);
  const [ticketsModalOpen, setTicketsModalOpen] = useState(false);
  const [companyPaymentOpen, setCompanyPaymentOpen] = useState(false);
  const [agentPaymentOpen, setAgentPaymentOpen] = useState(false);
  const [companiesList, setCompaniesList] = useState([]);
  const [driversList, setDriversList] = useState([]);
  const [unprocessedTickets, setUnprocessedTickets] = useState([]);
  const [financialState, setFinancialState] = useState({
    companies: {
      totalDue: 0,
      totalPaid: 0,
    },
    agents: {
      totalDue: 0,
      totalPaid: 0,
    }
  });

  const calculateCompanyDashboard = async () => {
    try {
      const colRef = collection(db, "financialState");
      const snapShot = await getDocs(colRef);
  
      if (!snapShot.empty) {
        const docSnap = snapShot.docs[0];
        const data = docSnap.data();
        setFinancialState(data)
      } else {
        console.error("No financial datas")
      }
    } catch (error) {
      console.log("Error calculateCompanyDashboard", error)
    }
  };

  const getUserFinancialState = (userDocData) => {
    const fs = userDocData.financialState || {}; // might not exist
    return {
      totalDue: fs.totalDue || 0,
      totalPaid: fs.totalPaid || 0,
      remaining: (fs.totalDue || 0) - (fs.totalPaid || 0),
      lastPayment: fs.lastPayment || { amount: 0, date: null },
      unpaidTickets: fs.unpaidTickets || 0,
    };
  };

  const fetchAllUsersFinancials = async (type) => {
    try {
      const snapshot = await getDocs(collection(db, type));

      const result = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const financial = getUserFinancialState(data);

        return {
          id: docSnap.id,
          name: type === "clients"
            ? data.enterprise
            : `${data.firstName} ${data.lastName}`,
          ...financial
        };
      });

    type === "clients" ? setCompaniesList(result) : setDriversList(result);
    } catch (err) {
      console.error("Error fetching users financials:", err);
      return [];
    }
  };

  const fetchUnprocessedTickets = async () => {
    try {
      const ticketsArray = [];

      //const today = new Date("2026-01-01T00:00:00");

      const q = query(
        collection(db, "tickets"),
        where("rateApplied", "==", null),
        limit(60)
        //where("createdAt", ">=", today)
      );

      const querySnapshot = await getDocs(q);

      for (const docSnap of querySnapshot.docs) {
        const ticketData = { ...docSnap.data(), uid: docSnap.id };

        if (ticketData?.client?.uid) {
          const clientDoc = await getDoc(doc(db, "clients", ticketData.client.uid));
          if (clientDoc.exists()) {
            const clientData = clientDoc.data();
            ticketData.companyRates = clientData.companyRates || [];
            ticketData.agentRates = clientData.agentRates || [];
          } else {
            ticketData.companyRates = [];
            ticketData.agentRates = [];
          }
        }

        ticketsArray.push(ticketData);
      }

      console.log("Tickets fetched:", ticketsArray.length);
      setUnprocessedTickets(ticketsArray);
    } catch (err) {
      console.error("fetchUnprocessedTickets error:", err);
    }
  };

  const updateFinancialState = async (updates) => {
    try {
      const colRef = collection(db, "financialState");
      const snap = await getDocs(colRef);
  
      if (snap.empty) {
        console.error("Financial state collection is empty");
        return;
      }
  
      const fsDocRef = snap.docs[0].ref;
      const firestoreUpdates = {};
  
      Object.entries(updates).forEach(([entity, values]) => {
        Object.entries(values).forEach(([key, amount]) => {
          firestoreUpdates[`${entity}.${key}`] = increment(amount);
        });
      });
  
      await updateDoc(fsDocRef, firestoreUpdates);
      const updatedSnap = await getDoc(fsDocRef);
      setFinancialState(updatedSnap.data());
    } catch (error) {
      console.log("updateFinancialState", error)
    }
  };

  const updateCompanyDataByTicket = async (ticket) => {
    try {
      if (ticket.client) {
        const companyRef = doc(db, "clients", ticket.client.uid);
        await updateDoc(companyRef, {
          "financialState.totalDue": increment(ticket.rateApplied.companyRate.amount),
        });
        setCompaniesList(prev =>
          prev.map(c =>
            c.id === ticket.client.uid
              ? {
                  ...c,
                  totalDue: c.totalDue + ticket.rateApplied?.companyRate?.amount,
                  remaining: c.remaining + ticket.rateApplied?.companyRate?.amount,
                }
              : c
          )
        );
      }
    } catch (error) {
      console.log("Error updateCompanyDataByTicket", error)
    }
  }

  const updateAgentDataByTicket = async (ticket) => {
    try {
      if (ticket.driver) {
        const driverRef = doc(db, "drivers", ticket.driver.id);
        await updateDoc(driverRef, {
          "financialState.totalDue": increment(ticket.rateApplied.agentRate.amount),
        });
        setDriversList(prev =>
          prev.map(d =>
            d.id === ticket.driver.id
              ? {
                  ...d,
                  totalDue: d.totalDue + ticket.rateApplied?.agentRate?.amount,
                  remaining: d.remaining + ticket.rateApplied?.agentRate?.amount,
                }
              : d
          )
        );
      }
    } catch(error) {
      console.log("updateAgentDataByTicket", error)
    }
  }

  const onCompanyPaid = async (id, amount) => {
    setCompaniesList(prev =>
      prev.map(c =>
        c.id === id
          ? {
              ...c,
              totalPaid: c.totalPaid + amount,
              remaining: c.remaining - amount,
            }
          : c
      )
    );
    try {
      const companyRef = doc(db, "clients", id);
      await updateDoc(companyRef, {
        "financialState.totalPaid": increment(amount),
        "financialState.lastPayment": {
          amount,
          date: serverTimestamp()
        }
      });
      await updateFinancialState({
        companies: {
          totalPaid: amount
        }
      });
    } catch (error) {
      console.log("onCompanyPaid", error)
    }
  }

  const onAgentPaid = async (id, amount) => {
    setDriversList(prev =>
      prev.map(c =>
        c.id === id
          ? {
              ...c,
              totalPaid: c.totalPaid + amount,
              remaining: c.remaining - amount,
            }
          : c
      )
    );
    try {
      const companyRef = doc(db, "drivers", id);
      await updateDoc(companyRef, {
        "financialState.totalPaid": increment(amount),
          "financialState.lastPayment": {
            amount,
            date: serverTimestamp()
          }
      });
      await updateFinancialState({
        agents: {
          totalPaid: amount
        }
      });
    } catch (error) {
      console.log("onAgentPaid", error)
    }
  }

useEffect(() => {
  console.log("Testing with new updates 6");

  const init = async () => {
    try {
      await Promise.all([
        fetchUnprocessedTickets(),
        calculateCompanyDashboard(),
        fetchAllUsersFinancials("clients"),
        fetchAllUsersFinancials("drivers"),
      ]);
    } catch (err) {
      console.error("Init error:", err);
    }
  };

  init();
}, []);

  /*
    Graph datas here
    */
  const entreprisesChart = {
    labels: companiesList.map((e) => e.name),
    datasets: [
      {
        label: "Montant dû",
        data: companiesList.map((e) => e.remaining),
        backgroundColor: "rgba(54, 162, 235, 0.7)"
      },
      {
        label: "Montant payé",
        data: companiesList.map((e) => e.totalPaid || 0),
        backgroundColor: "rgba(75, 192, 192, 0.7)"
      },
      {
        label: "Total",
        data: companiesList.map((e) => e.totalDue),
        backgroundColor: "#FF2222"
      },
    ],
  };

  const agentsChart = {
    labels: driversList.map((a) => `${a.name}`),
    datasets: [
      {
        label: "Montant dû",
        data: driversList.map((e) => e.remaining),
        backgroundColor: "rgba(54, 162, 235, 0.7)"
      },
      {
        label: "Montant payé",
        data: driversList.map((e) => e.totalPaid || 0),
        backgroundColor: "rgba(75, 192, 192, 0.7)"
      },
      {
        label: "Total",
        data: driversList.map((e) => e.totalDue),
        backgroundColor: "#FF2222"
      },

    ],
  };

  const filteredCompanies = companiesList.filter((e) =>
    e.name.toLowerCase().includes(searchEntreprise.toLowerCase())
  );

  const filteredDrivers = driversList.filter((a) =>
    a.name.toLowerCase().includes(searchAgent.toLowerCase())
  );

  const searchbarStyle = {
    width: "100%",
    height: "35px",
    fontSize: "15px",
    padding: "5px",
    border: "1px solid #efefefff",
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 2
  }
  const tableContainerStyle = {
    maxHeight: 400,
    overflowY: "auto",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse"
  };

  const theadStyle = {
    position: "sticky",
    background: "#f4f4f4ff",
    zIndex: 1
  }

  const thTdStyle = {
    padding: "10px",
    borderBottom: "1px solid #eee",
    textAlign: "left",
    fontSize: "12px",
    color: "#3c3c3c",
    fontWeight: 600,
  };

  const thTdStyleSub = {
    fontWeight: 400
  }

  return (
    <>
      <h2 className="dashboard-title" style={{ marginBottom: 30 }}>Comptabilité</h2>
      {loading ? (
        <Spin size="large" />
      ) : (
        <div className="dashboard-container" style={{ display: "flex", flexDirection: "column", gap: "30px" }}>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: 20 }}>
            <Button type="primary" onClick={() => setTicketsModalOpen(true)}>({unprocessedTickets.length}) Tickets à traiter</Button>
            <Button onClick={() => setPricingOpen(true)}>Ajuster les tarifs</Button>
            <Button onClick={() => setCompanyPaymentOpen(true)}>Encaisser des paiements</Button>
            <Button onClick={() => setAgentPaymentOpen(true)}>Payer nos agents</Button>
            <Button onClick={() => navigate("/admin/invoices")}>Gestion des factures</Button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}>
            <div className="card card--top-dashboard">
              <div>
                <h4 className="title">Montant à encaisser</h4>
                <h4 className="subtitle">Ce que les entreprises doivent vous verser</h4>
              </div>
              <div>
                <div className="bigtotal">{formatAmount(financialState.companies.totalDue - financialState.companies.totalPaid)}</div>
                <div className="subtotal">Total à encaisser: {formatAmount(financialState.companies.totalDue)}</div>
                <div className="subtotal">Ce qu'ils ont déjà payé: {formatAmount(financialState.companies.totalPaid)}</div>
              </div>
            </div>
            <div className="card card--top-dashboard">
              <div>
                <h4 className="title">Montant à verser</h4>
                <h4 className="subtitle">Ce que vous devez verser aux agents</h4>
              </div>
              <div>
                <div className="bigtotal">{formatAmount(financialState.agents.totalDue - financialState.agents.totalPaid)}</div>
                <div className="subtotal">Total à payer: {formatAmount(financialState.agents.totalDue)}</div>
                <div className="subtotal">Ce qu'on a déjà payé: {formatAmount(financialState.agents.totalPaid)}</div>
              </div>
            </div>
          </div>

          {/* Middle Row: Graphs */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <div className="card" style={{ padding: 20, background: "#fff", borderRadius: 8, boxShadow: "0 2px 6px rgba(0,0,0,0.1)" }}>
              <h4>Entreprises</h4>
              <Bar data={entreprisesChart} options={{ responsive: true, plugins: { legend: { position: "top" } } }} />
            </div>
            <div className="card" style={{ padding: 20, background: "#fff", borderRadius: 8, boxShadow: "0 2px 6px rgba(0,0,0,0.1)" }}>
              <h4>Agents</h4>
              <Bar data={agentsChart} options={{ responsive: true, plugins: { legend: { position: "top" } } }} />
            </div>
          </div>

          {/* Bottom Row: Tables */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            {/* Entreprises */}
            <div className="card card--top-dashboard">
              <h4 className="title">Entreprises</h4>

              <input
                type="text"
                placeholder="Rechercher une entreprise..."
                value={searchEntreprise}
                onChange={(e) => setSearchEntreprise(e.target.value)}
                style={searchbarStyle}
              />
              <div style={tableContainerStyle} className="scrollable-table">
                <table style={tableStyle}>
                  <thead style={theadStyle}>
                    <tr>
                      <th style={thTdStyle}>Entreprise</th>
                      <th style={thTdStyle}>Montant dû</th>
                      {/*<th style={thTdStyle}>Tickets non payés</th>*/}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCompanies.map((e) => (
                      <tr key={e.id}>
                        <td style={{...thTdStyle, ...thTdStyleSub}}>{e.name}</td>
                        <td style={{...thTdStyle, ...thTdStyleSub}}>{formatAmount(e.remaining || 0)}</td>
                        {/*<td style={{...thTdStyle, ...thTdStyleSub}}>{e.unpaidTickets}</td>*/}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Agents */}
            <div className="card card--top-dashboard">
              <h4 className="title">Agents</h4>

              <input
                type="text"
                placeholder="Rechercher un agent..."
                value={searchAgent}
                onChange={(e) => setSearchAgent(e.target.value)}
                style={searchbarStyle}
              />
              <div style={tableContainerStyle} className="scrollable-table">
                <table style={tableStyle}>
                  <thead style={theadStyle}>
                    <tr>
                      <th style={thTdStyle}>Agent</th>
                      <th style={thTdStyle}>Montant dû</th>
                      {/*<th style={thTdStyle}>Tickets non payés</th>*/}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDrivers.map((a) => (
                      <tr key={a.id}>
                        <td style={{...thTdStyle, ...thTdStyleSub}}>{a.name}</td>
                        <td style={{...thTdStyle, ...thTdStyleSub}}>{formatAmount(a.remaining)}</td>
                        {/*<td style={{...thTdStyle, ...thTdStyleSub}}>{a.unpaidTickets}</td>*/}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Scroll moderne */}
            <style>
              {`
                .scrollable-table::-webkit-scrollbar {
                  width: 8px;
                }
                .scrollable-table::-webkit-scrollbar-track {
                  background: #f1f1f1;
                  border-radius: 4px;
                }
                .scrollable-table::-webkit-scrollbar-thumb {
                  background: #888;
                  border-radius: 4px;
                }
                .scrollable-table::-webkit-scrollbar-thumb:hover {
                  background: #555;
                }
              `}
            </style>
          </div>
        </div>
      )}

      <PricingRulesModal
        open={pricingOpen}
        onClose={() => setPricingOpen(false)}
      />
      <CompanyPaymentModal
        open={companyPaymentOpen}
        onClose={() => setCompanyPaymentOpen(false)}
        companies={companiesList}
        onPaymentSaved={(companyId, amount) => {
          onCompanyPaid(companyId, amount)
        }}
      />

      <AgentPaymentsModal
        open={agentPaymentOpen}
        onClose={() => setAgentPaymentOpen(false)}
        drivers={driversList}
        onPaymentSaved={(driverId, amount) => {
          onAgentPaid(driverId, amount)
        }}
      />

      <UnprocessedTicketsModal
        open={ticketsModalOpen}
        tickets={unprocessedTickets}
        onRateAapplied={(ticket) => {
          setUnprocessedTickets(prev => prev.filter(t => t.uid !== ticket.uid));
          updateFinancialState({
            companies: {
              totalDue: ticket.rateApplied.companyRate.amount
            },
            agents: {
              totalDue: ticket.rateApplied.agentRate.amount
            }
          });
          updateCompanyDataByTicket(ticket);
          updateAgentDataByTicket(ticket);
        }}
        onClose={() => setTicketsModalOpen(false)}
      />

    </>
  );
}
